// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Glint — the three modes (principle #14). The RULE SET is the axis that changes the game,
// not a dialled number:
//   · Sprint — clear the WHOLE deck (order 5, 6 symbols/card). Race the deck; score rewards
//              speed and an unbroken combo. A wrong tap only breaks the streak.
//   · Blaze  — 90 seconds, most cards cleared (order 7, 8 symbols/card — a denser scan).
//              Race the CLOCK, not the deck. A wrong tap costs time.
//   · Streak — ONE life (order 5). A single wrong tap ENDS the run. Sudden-death precision,
//              a structurally different risk curve — nerve beats frantic tapping here.
//
// The host's chosen mode travels FROZEN inside the round start (rematch roundOpts); guests
// render state().hostOpts. An id off the wire is always modeOf()-validated so an unknown
// value falls back to Sprint rather than reaching the deck builder as undefined (guarded
// with Object.hasOwn — a wire key must never resolve a prototype member).

export type ModeId = 'sprint' | 'blaze' | 'streak';

export type Clock = 'up' | 'down';

export interface Mode {
  readonly id: ModeId;
  readonly name: string;
  readonly blurb: string;
  /** Projective-plane order → n+1 symbols per card, n²+n+1 cards. */
  readonly order: number;
  /** 'up' counts elapsed (Sprint/Streak); 'down' drains from durationMs (Blaze). */
  readonly clock: Clock;
  /** Blaze only: the round length. 0 otherwise. */
  readonly durationMs: number;
  /** Streak: a single wrong tap ends the run. */
  readonly sudden: boolean;
  /** Sprint: the goal is to clear every pair in the deck. */
  readonly finite: boolean;
}

export const MODES: Record<ModeId, Mode> = {
  sprint: {
    id: 'sprint',
    name: 'Sprint',
    blurb: 'Clear the whole deck as fast as you can. Six symbols a card. Keep the combo alive.',
    order: 5,
    clock: 'up',
    durationMs: 0,
    sudden: false,
    finite: true,
  },
  blaze: {
    id: 'blaze',
    name: 'Blaze',
    blurb: 'Ninety seconds, eight symbols a card. Grab as many as you can — a miss costs time.',
    order: 7,
    clock: 'down',
    durationMs: 90_000,
    sudden: false,
    finite: false,
  },
  streak: {
    id: 'streak',
    name: 'Streak',
    blurb: 'One life. One wrong tap ends it. Every clean tap multiplies — how far dare you go?',
    order: 5,
    clock: 'up',
    durationMs: 0,
    sudden: true,
    finite: false,
  },
};

export const MODE_IDS: ModeId[] = ['sprint', 'blaze', 'streak'];

export const DEFAULT_MODE: Mode = MODES.sprint;

/** Resolve a (possibly untrusted, off-the-wire) id to a Mode; unknown → Sprint. */
export function modeOf(id: unknown): Mode {
  if (typeof id === 'string' && Object.hasOwn(MODES, id)) {
    return MODES[id as ModeId];
  }
  return DEFAULT_MODE;
}
